import { reportWebAction, WebActionName, WebActionSource, } from "@snapchat/web-blizzard-reporting";
import { CrossIcon, IconButton, Sizes } from "@snapchat/web-core-ui";
import { isErrorState, isLensInactive, LiveLensPreview as InteractiveLensPreview, LiveLensState, } from "@snapchat/web-live-lens";
import { getDeviceName, incrementCounter } from "@snapchat/web-graphene-reporting";
import clsx from "clsx";
import { useCallback, useContext, useRef, useState, } from "react";
import { useIntl } from "react-intl";
import { BlurredIconTile } from "../../common/ImageTile/BlurredIconTile";
import { SpotlightForLensesOption } from "../../common/Shell/webConfigContext";
import { DESKTOP_LENS_ALLOWLIST, MOBILE_LENS_ALLOWLIST } from "../LensProfile/liveLensConfig";
import { MobileLensFooter, } from "../LensProfile/MobileLensProfile/MobileLensFooter";
import { TryLensCTA } from "../TryLensCTA";
import styles from "./LensPreviewCard.module.css";
import { grafanaBasePrefix } from "../../../lib/grafana";
import { PageContext, LayoutContext, EmbedLayoutContext } from "@snapchat/web-frame";
export function LensPreviewCard({ lensGroupId, lensId, lensName, pageType, previewImageUrl, previewVideoUrl, lensCreatorTags, iconUrl, locale, isMobile, snapcodeImageUrl, spotlightsUsingThisLens, spotlightOption, lensCreatorDisplayName, userProfileUrl, openInSnapchatLink, moreLenses, setLiveLensEnabled, }) {
    const { webClientId } = useContext(PageContext);
    const [liveLensState, setLiveLensState] = useState(LiveLensState.INACTIVE);
    const lensTryOnCountRef = useRef(0);
    const deviceName = getDeviceName(isMobile);
    const layoutConfig = useContext(LayoutContext);
    const isEmbed = layoutConfig[EmbedLayoutContext] === "true";
    /* istanbul ignore next : Remove once test code is added */
    const onTryLensClick = useCallback(() => {
        // Error states are re-tryable so we include them in conditional statement for onTryLensClick
        if (liveLensState === LiveLensState.INACTIVE || isErrorState(liveLensState)) {
            setLiveLensState(LiveLensState.REQUESTED);
            lensTryOnCountRef.current = lensTryOnCountRef.current + 1;
            void incrementCounter(`${grafanaBasePrefix}.LiveLens.TryOnCountPerSession.${isEmbed ? "Embed." : ""}${deviceName}`, lensTryOnCountRef.current);
            reportWebAction({
                pageType,
                actionName: WebActionName.TryOnLiveLenses,
                webClientId: webClientId ?? "",
                actionSource: isEmbed ? WebActionSource.Embed_TryLiveLens : "",
            });
            setLiveLensEnabled?.();
        }
    }, [deviceName, liveLensState, pageType, webClientId, isEmbed, setLiveLensEnabled]);
    const isLiveLensAllowListed = isMobile
        ? MOBILE_LENS_ALLOWLIST.includes(lensId)
        : DESKTOP_LENS_ALLOWLIST.includes(lensId);
    const mobileFooterProps = {
        spotlightsUsingThisLens: spotlightsUsingThisLens || [],
        openInSnapchatLink: openInSnapchatLink || "",
        spotlightOption: spotlightOption || SpotlightForLensesOption.disabled,
        lensCreatorDisplayName: lensCreatorDisplayName || "",
        lensCreatorTags,
        userProfileUrl: userProfileUrl || "",
        lensName,
        snapcodeImageUrl,
        iconUrl,
        onTryLensClick,
        liveLensState,
        pageType,
        moreLenses,
    };
    return (<div className={clsx(isMobile ? styles.previewCardMobile : styles.previewCard, {
            [styles.embedPreview]: isEmbed,
        })}>
      {liveLensState !== LiveLensState.APPLIED &&
            (previewVideoUrl ? (<video className={styles.previewVideo} src={previewVideoUrl} poster={previewImageUrl} playsInline autoPlay loop muted/>) : (<InactiveLensPreview previewImageUrl={previewImageUrl} lensName={lensName} iconUrl={iconUrl} isMobile={isMobile}/>))}
      {isLiveLensAllowListed && (<LiveLensPreview lensId={lensId} lensGroupId={lensGroupId} locale={locale} isMobile={isMobile} isLiveLensAllowListed={isLiveLensAllowListed} setLiveLensState={setLiveLensState} canTryLens={true} setLiveLensEnabled={setLiveLensEnabled} {...mobileFooterProps}/>)}
      {!isLiveLensAllowListed && isMobile && !isEmbed && (<MobileLensFooter {...mobileFooterProps} canTryLens={false}/>)}
    </div>);
}
function LiveLensPreview(props) {
    const { lensId, lensGroupId, pageType, locale, lensName, isMobile, spotlightsUsingThisLens, openInSnapchatLink, spotlightOption, lensCreatorDisplayName, lensCreatorTags, userProfileUrl, snapcodeImageUrl, iconUrl, moreLenses, isLiveLensAllowListed, liveLensState, setLiveLensState, onTryLensClick, setLiveLensEnabled, } = props;
    /* istanbul ignore next : Remove once test code is added */
    const onCloseClick = useCallback(() => {
        if (liveLensState !== LiveLensState.INACTIVE) {
            setLiveLensState(LiveLensState.INACTIVE);
        }
        setLiveLensEnabled?.();
    }, [liveLensState, setLiveLensState, setLiveLensEnabled]);
    const layoutConfig = useContext(LayoutContext);
    const isEmbed = layoutConfig[EmbedLayoutContext] === "true";
    const mobileFooterProps = {
        spotlightsUsingThisLens: spotlightsUsingThisLens,
        openInSnapchatLink: openInSnapchatLink || "",
        spotlightOption: spotlightOption || SpotlightForLensesOption.disabled,
        lensCreatorDisplayName: lensCreatorDisplayName || "",
        lensCreatorTags,
        userProfileUrl: userProfileUrl || "",
        lensName,
        snapcodeImageUrl,
        iconUrl,
        onTryLensClick,
        liveLensState,
        pageType,
        moreLenses,
    };
    /* istanbul ignore next : Remove once test code is added */
    const closeButton = isLiveLensAllowListed && liveLensState === LiveLensState.APPLIED ? (<CloseButton onCloseClick={onCloseClick} isMobile={isMobile} isEmbed={isEmbed}/>) : null;
    let interactiveLivePreview;
    /* istanbul ignore next -- see WEB-2785 */
    if (liveLensState !== LiveLensState.INACTIVE) {
        interactiveLivePreview = (<InteractiveLensPreview liveLensState={liveLensState} setLiveLensState={setLiveLensState} lensId={lensId} lensGroupId={lensGroupId} pageType={pageType} locale={locale} lensName={lensName} isMobile={isMobile}/>);
    }
    return (<>
      {interactiveLivePreview}
      {isLiveLensAllowListed && isLensInactive(liveLensState) && (!isMobile || isEmbed) && (<TryLensCTA onClick={onTryLensClick} lensName={lensName} isMobile={false}/>)}
      {closeButton}
      {isMobile && !isEmbed && <MobileLensFooter {...mobileFooterProps} canTryLens={false}/>}
    </>);
}
/* istanbul ignore next : Remove once test code is added */
function CloseButton({ onCloseClick, isMobile, isEmbed, }) {
    return (<span className={clsx(styles.closeButtonWrapper, {
            [styles.closeButtonWrapperMobile]: isMobile,
            [styles.closeButtonWrapperEmbeds]: isEmbed,
        })} data-testid="livelens-close">
      <IconButton onClick={onCloseClick} size="XSMALL" label="Close">
        <CrossIcon size={Sizes.Large}/>
      </IconButton>
    </span>);
}
function InactiveLensPreview({ previewImageUrl, iconUrl, lensName, isMobile, }) {
    const intl = useIntl();
    const previewAlt = intl.formatMessage({
        description: "Snapchat Lens Tile Image",
        defaultMessage: "Snapchat Lens Tile Image for {lensName}",
    }, { lensName });
    const blurredTileAlt = intl.formatMessage({
        description: "Snapchat Lens Icon",
        defaultMessage: "Snapchat Lens Icon for {lensName} ",
    }, { lensName });
    return (<div data-testid="default-lens-preview-image-tile" className={styles.inactiveLensPreview}>
      {previewImageUrl ? (<img src={previewImageUrl} className={styles.previewImage} alt={previewAlt}/>) : (<div className={isMobile ? styles.defaultTileMobile : styles.defaultTile}>
          <BlurredIconTile iconUrl={iconUrl} alt={blurredTileAlt} size="large"/>
        </div>)}
    </div>);
}
